import * as React from 'react';
import axios from 'axios';
import replace from "buffer-replace";
import { saveAs } from "file-saver";
import { DownloadIcon } from '@patternfly/react-icons';
import { Spinner, Modal, ModalVariant, Button } from '@patternfly/react-core';
import './DownloadImage.css';
import { MdCloudDownload } from 'react-icons/md';
import UserSession from '@app/common/user-session';
import ApiGateway from '@app/api/api-gateway/api-gateway';
import '../app.css';
import { useSnackbar } from 'notistack';

// Global download registry to persist across re-renders
const downloadRegistry = new Map<string, {
  interval: NodeJS.Timeout;
  status: string;
  downloading: boolean;
  onUpdate: (status: string, downloading: boolean) => void;
}>();

// Helper function for error message sanitization
function getSanitizedErrorMessage(error, fallback) {
  // Check for HTTP 4xx status in response
  if (error?.response?.status >= 400 && error?.response?.status < 500) {
    return 'An error occurred while starting the download. Please check your request or permissions.';
  }
  // Check for status code in error.message string
  if (typeof error?.message === 'string' && /status code 4\d\d/.test(error.message)) {
    return 'An error occurred while starting the download. Please check your request or permissions.';
  }
  return error?.response?.data?.message || error?.message || fallback;
}

function isCancellationError(error) {
  return axios.isCancel?.(error) || error?.message === 'Download cancelled';
}

const FAILED_PACKAGE_STATUSES = new Set(['failed', 'error']);

function isFailedPackageStatus(status: unknown) {
  return typeof status === 'string' && FAILED_PACKAGE_STATUSES.has(status.trim().toLowerCase());
}

function getPackageFailureMessage(status: unknown) {
  const normalizedStatus = typeof status === 'string' ? status.trim().toLowerCase() : '';
  return `Package generation ${normalizedStatus || 'failed'}`;
}

function getDownloadKey(props) {
  const { imageType, imageId, downloadTypeOverride } = props;

  if (!imageId) {
    return `${imageType || 'unknown'}-unknown`;
  }

  if (imageType === 'site') {
    if (downloadTypeOverride === 'CAFlexARM64') {
      return `${imageType}-${imageId}-arm64`;
    }
    if (downloadTypeOverride === 'CAFlex') {
      return `${imageType}-${imageId}`;
    }
  }

  return `${imageType}-${imageId}`;
}

class DownloadImage extends React.Component<any, any> {
  private _isMounted: boolean = false;
  private cancelRequested: boolean = false;
  private completionTimeout: NodeJS.Timeout | null = null;
  private activeRequestCancelSource: any = null;

  constructor(props) {
    super(props);
    
    this.state = {
      imageType: this.props.imageType,
      downloading: false,
      isModalOpen: false,
      downloadType: null,
      pollInterval: null,
      downloadStatus: ''
    }
  }

  updateDownloadState = (status: string, downloading: boolean) => {
    if (this._isMounted) {
      this.setState({ downloadStatus: status, downloading });
    }

    if (this.props.onStatusChange) {
      this.props.onStatusChange({
        status,
        downloading,
        downloadType: this.props.downloadTypeOverride || this.state.downloadType,
      });
    }
  }

  // Check if user has manager role
  hasManagerPermission = () => {
    const userRole = UserSession.getParam('role')?.toLowerCase();
    return userRole === 'manager';
  }

  handleDownload = async () => {
    if (this.props.disabled) {
      return;
    }

    // Check permissions before proceeding
    if (!this.hasManagerPermission()) {
      this.props.enqueueSnackbar("You do not have permission to perform this action", {
        variant: 'error',
        key: new Date().getTime() + Math.random()
      });
      return;
    }

    // If downloadTypeOverride is set, skip modal and start download directly
    if (this.props.downloadTypeOverride) {
      await this.startDownload(this.props.downloadTypeOverride);
      return;
    }

    if (this.props.imageType === "site") {
      this.setState({ isModalOpen: true });
      return;
    }

    await this.startDownload("CAImage");
  }

  startDownload = async (type) => {
    // Double-check permissions 
    if (!this.hasManagerPermission()) {
      this.props.enqueueSnackbar("You do not have permission to perform this action", { 
        variant: 'error',
        key: new Date().getTime() + Math.random()
      });
      return;
    }
    
    const { imageId, imageType } = this.props;
    
    try {
      this.cancelRequested = false;
      this.setState({ downloadType: type });
      if (this.props.onDownloadStart) {
        this.props.onDownloadStart(type);
      }

      if (type === "CAFlex" || type === "CAFlexARM64") {

        await new Promise<void>(resolve => {
          this.setState({ downloading: true }, resolve);
        });
        try {
          // Close modal only after download has successfully started
          this.setState({ isModalOpen: false });
          await this.startCAFlexDownload(imageId, type === "CAFlexARM64" ? "arm64" : undefined);
        } catch(error: any) {
          if (isCancellationError(error)) {
            return;
          }
          if (this._isMounted) {
            this.setState({ downloading: false, isModalOpen: false });
            this.props.enqueueSnackbar(getSanitizedErrorMessage(error, 'Network error'), {
              variant: 'error',
              key: new Date().getTime() + Math.random()
            });
          }
        }
        return;
      }

      this.setState({ isModalOpen: false });

      switch (imageType) {
        case "server": 
          await new Promise<void>(resolve => {
            this.setState({ downloading: true }, resolve);
          });
          try {
            await this.generateServerImage(imageId);
            if (this._isMounted) {
              this.setState({ downloading: false });
            }
          } catch(error: any) {
            if (isCancellationError(error)) {
              return;
            }
            if (this._isMounted) {
              this.setState({ downloading: false });
              this.props.enqueueSnackbar(getSanitizedErrorMessage(error, 'Network error'), { 
                variant: 'error',
                key: new Date().getTime() + Math.random()
              });
            }
          }
          break;
        case "site": 
          await new Promise<void>(resolve => {
            this.setState({ downloading: true }, resolve);
          });
          try {
            await this.generateSiteImage(imageId);
            if (this._isMounted) {
              this.setState({ downloading: false });
            }
          } catch(error: any) {
            if (isCancellationError(error)) {
              return;
            }
            if (this._isMounted) {
              this.setState({ downloading: false });
              this.props.enqueueSnackbar(getSanitizedErrorMessage(error, 'Network error'), { 
                variant: 'error',
                key: new Date().getTime() + Math.random()
              });
            }
          }
          break;
        case "controller": 
          await new Promise<void>(resolve => {
            this.setState({ downloading: true }, resolve);
          });
          try {
            await this.downloadControllerPackage(imageId);
            
          } catch(error: any) {
            if (isCancellationError(error)) {
              return;
            }
            // downloadControllerPackage handles its own downloading state management
            if (this._isMounted && this.props.enqueueSnackbar) {
              this.props.enqueueSnackbar(getSanitizedErrorMessage(error, 'Network error'), { 
                variant: 'error',
                key: new Date().getTime() + Math.random()
              });
            }
          }
          break;
        default: 
          if (this._isMounted) {
            this.props.enqueueSnackbar("Unsupported image type for download", { 
              variant: 'error',
              key: new Date().getTime() + Math.random()
            });
          }
      }
    } catch (error: any) {
      if (isCancellationError(error)) {
        return;
      }
      if (this._isMounted) {
        this.setState({ downloading: false, isModalOpen: false });
        this.props.enqueueSnackbar(getSanitizedErrorMessage(error, 'An unexpected error occurred'), { 
          variant: 'error',
          key: new Date().getTime() + Math.random()
        });
      }
    }
  }

  startCAFlexDownload = async (siteId, arch?: string) => {
    const downloadKey = `${this.props.imageType}-${siteId}${arch ? `-${arch}` : ''}`;
    const packagesPath = arch
      ? `/sites/${siteId}/packages?arch=${arch}`
      : `/sites/${siteId}/packages`;

    try {
      // Check if download is already in progress
      if (downloadRegistry.has(downloadKey)) {
        return; // Download already in progress
      }

      // Set downloading state first
      if (this._isMounted) {
        this.setState({ downloading: true });
      }

      // Initial API call
      const response = await ApiGateway.get(packagesPath);

      if (isFailedPackageStatus(response.data.status)) {
        throw new Error(getPackageFailureMessage(response.data.status));
      }

      // Set initial status
      if (this._isMounted) {
        this.updateDownloadState(response.data.status, true);
      }

      // Create global download entry
      const pollInterval = setInterval(async () => {
        try {
          if (this.cancelRequested) {
            clearInterval(pollInterval);
            return;
          }

          const statusResponse = await ApiGateway.get(packagesPath);
          if (this.cancelRequested) {
            clearInterval(pollInterval);
            return;
          }
          const status = statusResponse.data.status;

          if (isFailedPackageStatus(status)) {
            throw new Error(getPackageFailureMessage(status));
          }
          
          // Update the global registry
          const downloadEntry = downloadRegistry.get(downloadKey);
          if (downloadEntry) {
            downloadEntry.status = status;
            downloadEntry.onUpdate(status, true);
          }

          if (status === "Completed") {
            clearInterval(pollInterval);
            
            const packageUrl = statusResponse.data.package?.url;
            if (!packageUrl) {
              throw new Error('Error downloading the installation package');
            }
            
            // Handle the download
            const link = document.createElement('a');
            link.href = packageUrl;
            link.download = `caflex${arch ? `-${arch}` : ''}-${siteId.replace(/-/g, "")}.deb`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            // Update UI to show completion
            if (downloadEntry) {
              downloadEntry.onUpdate('', false);
            }
            
            // Clean up registry
            downloadRegistry.delete(downloadKey);
          }
        } catch (error: any) {
          clearInterval(pollInterval);
          if (this.cancelRequested) {
            downloadRegistry.delete(downloadKey);
            return;
          }
          
          const downloadEntry = downloadRegistry.get(downloadKey);
          if (downloadEntry) {
            downloadEntry.onUpdate('', false);
          }
          
          downloadRegistry.delete(downloadKey);
          
          // Show error using the current component's snackbar if available
          if (this._isMounted) {
            this.props.enqueueSnackbar(getSanitizedErrorMessage(error, 'Network error'), { 
              variant: 'error',
              key: new Date().getTime() + Math.random()
            });
          }
        }
      }, 5000); 

      // Register the download in the global registry
      downloadRegistry.set(downloadKey, {
        interval: pollInterval,
        status: response.data.status,
        downloading: true,
        onUpdate: (status: string, downloading: boolean) => {
          this.updateDownloadState(status, downloading);
        }
      });
      if (this._isMounted) {
        this.setState({ pollInterval });
      }
    } catch (error: any) {
      // Clean up registry on initialization error
      downloadRegistry.delete(downloadKey);
      this.updateDownloadState('', false);
      
      if (this._isMounted) {
        this.setState({ 
          downloading: false, 
          downloadStatus: ''
        });
        this.props.enqueueSnackbar(getSanitizedErrorMessage(error, 'Network error'), { 
          variant: 'error',
          key: new Date().getTime() + Math.random()
        });
      }
    }
  }

  downloadControllerPackage = async (controllerId: string, retryCount = 0) => {
    const downloadKey = `${this.props.imageType}-${controllerId}`;
    const maxRetries = 15; // Increased to 15 for longer package generation
    const baseRetryDelay = 5000; // Check every 5 seconds
    // Progressive delay: longer waits for later attempts
    const retryDelay = retryCount < 5 ? baseRetryDelay : baseRetryDelay + (retryCount - 4) * 1000;
    
    try {
      if (this.cancelRequested) {
        const activeDownload = downloadRegistry.get(downloadKey);
        if (activeDownload) {
          activeDownload.onUpdate('', false);
        }
        downloadRegistry.delete(downloadKey);
        return Promise.resolve();
      }

      // Check if download is already in progress
      if (downloadRegistry.has(downloadKey) && retryCount === 0) {
        return; // Download already in progress
      }
      const token = UserSession.getParam('token');
      
      if (!token) {
        throw new Error('No authentication token available');
      }

      // Set initial downloading state and register download if first attempt
      if (retryCount === 0) {
        if (this._isMounted) {
          this.setState({ downloading: true });
        }
        
        // Register the download in the global registry
        downloadRegistry.set(downloadKey, {
          interval: null as any, // Will be set later if needed
          status: '',
          downloading: true,
          onUpdate: (status: string, downloading: boolean) => {
            this.updateDownloadState(status, downloading);
          }
        });
      }

      // Add delay to show processing status
      await new Promise(resolve => setTimeout(resolve, 400));
      if (this.cancelRequested) {
        const activeDownload = downloadRegistry.get(downloadKey);
        if (activeDownload) {
          activeDownload.onUpdate('', false);
        }
        downloadRegistry.delete(downloadKey);
        return Promise.resolve();
      }

      // GET starts package generation when needed and returns its current status.
      const timestamp = new Date().getTime();
      const response = await axios({
        method: 'get',
        url: `${process.env.REACT_APP_API_URL}/controllers/${controllerId}/packages?arch=amd64&t=${timestamp}`,
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (isFailedPackageStatus(response.data.status)) {
        throw new Error(getPackageFailureMessage(response.data.status));
      }

      // Set initial status from API response
      if (retryCount === 0) {
        const downloadEntry = downloadRegistry.get(downloadKey);
        if (downloadEntry) {
          downloadEntry.status = response.data.status;
          downloadEntry.onUpdate(response.data.status, true);
        }
      }

      // Parse the response to get the package URL
      const packageData = response.data.package;
      
      if (!packageData) {
        // If package is not ready and we haven't exceeded max retries, try again
        if (retryCount < maxRetries) {
          const apiStatus = response.data.status || 'unknown';
          let statusMessage = apiStatus;
          
          // Update the global registry
          const downloadEntry = downloadRegistry.get(downloadKey);
          if (downloadEntry) {
            downloadEntry.status = statusMessage;
            downloadEntry.onUpdate(statusMessage, true);
          }
          
          await new Promise(resolve => setTimeout(resolve, retryDelay));
          if (this.cancelRequested) {
            if (downloadEntry) {
              downloadEntry.onUpdate('', false);
            }
            downloadRegistry.delete(downloadKey);
            return Promise.resolve();
          }
          return this.downloadControllerPackage(controllerId, retryCount + 1);
        }
        
        const status = response.data.status || 'unknown';
        
        // Clean up registry before throwing error
        const downloadEntry = downloadRegistry.get(downloadKey);
        if (downloadEntry) {
          downloadEntry.onUpdate('', false);
        }
        downloadRegistry.delete(downloadKey);
        
        if (status === 'In Progress' || status === 'Pending') {
          const totalTime = Math.round(maxRetries * 2);
          throw new Error(`Package generation is taking longer than expected (${totalTime}s). Please try again in a few minutes.`);
        } else {
          throw new Error(`No package available for this controller after ${maxRetries + 1} attempts. Status: ${status}.`);
        }
      }
      
      const packageUrl = packageData.url;
      
      if (!packageUrl) {
        // Package exists but URL not ready yet, continue retrying if we haven't exceeded max retries
        if (retryCount < maxRetries) {
          const apiStatus = response.data.status || 'unknown';
          
          // Update the global registry
          const downloadEntry = downloadRegistry.get(downloadKey);
          if (downloadEntry) {
            downloadEntry.status = apiStatus;
            downloadEntry.onUpdate(apiStatus, true);
          }
          
          await new Promise(resolve => setTimeout(resolve, retryDelay));
          if (this.cancelRequested) {
            if (downloadEntry) {
              downloadEntry.onUpdate('', false);
            }
            downloadRegistry.delete(downloadKey);
            return Promise.resolve();
          }
          return this.downloadControllerPackage(controllerId, retryCount + 1);
        } else {
          // Exceeded max retries, clean up and exit silently
          const downloadEntry = downloadRegistry.get(downloadKey);
          if (downloadEntry) {
            downloadEntry.onUpdate('', false);
          }
          downloadRegistry.delete(downloadKey);
          return Promise.resolve();
        }
      }

      // Update status to downloading via registry
      const downloadingEntry = downloadRegistry.get(downloadKey);
      if (downloadingEntry) {
        downloadingEntry.status = 'Downloading';
        downloadingEntry.onUpdate('Downloading', true);
      }

      // Add delay to show downloading status
      await new Promise(resolve => setTimeout(resolve, 300));

      // Extract filename from the URL
      const urlParts = packageUrl.split('/');
      const filename = urlParts[urlParts.length - 1].split('?')[0] || `controller-${controllerId.replace(/-/g, "")}.deb`;

      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = packageUrl;
      link.download = filename;
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Update status to completed via registry
      const completedEntry = downloadRegistry.get(downloadKey);
      if (completedEntry) {
        completedEntry.status = 'Completed';
        completedEntry.onUpdate('Completed', true);
      }

      // Clear status after showing completed and stop downloading animation
      this.completionTimeout = setTimeout(() => {
        const entry = downloadRegistry.get(downloadKey);
        if (entry) {
          entry.onUpdate('', false);
        }
        // Clean up registry
        downloadRegistry.delete(downloadKey);
      }, 1000);
      
      return Promise.resolve();
    } catch (error) {
      // Clear status on error and stop downloading animation via registry
      const downloadEntry = downloadRegistry.get(downloadKey);
      if (downloadEntry) {
        downloadEntry.onUpdate('', false);
      }
      // Clean up registry
      downloadRegistry.delete(downloadKey);
      
      // error handling 
      throw new Error(getSanitizedErrorMessage(error, 'Network error'));
    }
  };

  generateServerImage = async (id) => {
    try {
      // Set initial status
      if (this._isMounted) {
        this.updateDownloadState('Processing', true);
      }

      // Add delay to show processing status
      await new Promise(resolve => setTimeout(resolve, 300));
      if (this.cancelRequested) {
        throw new Error('Download cancelled');
      }

      this.activeRequestCancelSource = axios.CancelToken.source();

      const { data: raw } = await axios.get(process.env.REACT_APP_SERVER_IMAGE_URL!, {
        responseType: "arraybuffer",
        cancelToken: this.activeRequestCancelSource.token,
      });
      this.activeRequestCancelSource = null;
      if (this.cancelRequested) {
        throw new Error('Download cancelled');
      }

      // Update status to generating
      if (this._isMounted) {
        this.updateDownloadState('Generating', true);
      }

      // Add delay to show generating status
      await new Promise(resolve => setTimeout(resolve, 400));
      if (this.cancelRequested) {
        throw new Error('Download cancelled');
      }

      const theId = id.replace(/-/g, "");
      const replaced = replace(raw, "ethica_machine_id_placeholder___", theId);
      const blob = new Blob([replaced], { type: "charset=binary" });
      
      // Update status to downloading
      if (this._isMounted) {
        this.updateDownloadState('Downloading', true);
      }

      // Add delay to show downloading status
      await new Promise(resolve => setTimeout(resolve, 250));
      if (this.cancelRequested) {
        throw new Error('Download cancelled');
      }

      saveAs(blob, `server-${theId}.img`);

      // Update status to completed
      if (this._isMounted) {
        this.updateDownloadState('Completed', true);
      }

      // Clear status after showing completed
      setTimeout(() => {
        if (this._isMounted) {
          this.updateDownloadState('', false);
        }
      }, 1000);

      return Promise.resolve();
    } catch (error) {
      this.activeRequestCancelSource = null;
      // Clear status on error
      if (this._isMounted) {
        this.updateDownloadState('', false);
      }
      if (isCancellationError(error)) {
        return Promise.resolve();
      }
      throw error;
    }
  };

  generateSiteImage = async (id) => {
    try {
      // Set initial status
      if (this._isMounted) {
        this.updateDownloadState('Processing', true);
      }

      // Add delay to show processing status
      await new Promise(resolve => setTimeout(resolve, 300));
      if (this.cancelRequested) {
        throw new Error('Download cancelled');
      }

      this.activeRequestCancelSource = axios.CancelToken.source();

      const { data: raw } = await axios.get(process.env.REACT_APP_CPE_IMAGE_URL!, {
        responseType: "arraybuffer",
        cancelToken: this.activeRequestCancelSource.token,
      });
      this.activeRequestCancelSource = null;
      if (this.cancelRequested) {
        throw new Error('Download cancelled');
      }

      // Update status to generating
      if (this._isMounted) {
        this.updateDownloadState('Generating', true);
      }

      // Add delay to show generating status
      await new Promise(resolve => setTimeout(resolve, 400));
      if (this.cancelRequested) {
        throw new Error('Download cancelled');
      }

      const theId = id.replace(/-/g, "");
      const replaced = replace(raw, "ethica_machine_id_placeholder___", theId);
      const blob = new Blob([replaced], { type: "charset=binary" });
      
      // Update status to downloading
      if (this._isMounted) {
        this.updateDownloadState('Downloading', true);
      }

      // Add delay to show downloading status
      await new Promise(resolve => setTimeout(resolve, 250));
      if (this.cancelRequested) {
        throw new Error('Download cancelled');
      }

      saveAs(blob, `cpe-${theId}.img`);

      // Update status to completed
      if (this._isMounted) {
        this.updateDownloadState('Completed', true);
      }

      // Clear status after showing completed
      setTimeout(() => {
        if (this._isMounted) {
          this.updateDownloadState('', false);
        }
      }, 1000);

      return Promise.resolve();
    } catch (error) {
      this.activeRequestCancelSource = null;
      // Clear status on error
      if (this._isMounted) {
        this.updateDownloadState('', false);
      }
      if (isCancellationError(error)) {
        return Promise.resolve();
      }
      throw error;
    }
  };

  handleModalClose = () => {
    this.setState({ isModalOpen: false });
  }

  stubCancelDownloadRequest = async () => {
    // TODO: call the backend cancellation API when it is available.
    return Promise.resolve();
  }

  cancelDownload = async () => {
    this.cancelRequested = true;

    const downloadKey = getDownloadKey({
      ...this.props,
      downloadTypeOverride: this.state.downloadType || this.props.downloadTypeOverride,
    });
    const activeDownload = downloadRegistry.get(downloadKey);

    if (activeDownload?.interval) {
      clearInterval(activeDownload.interval);
    }
    if (this.state.pollInterval) {
      clearInterval(this.state.pollInterval);
    }
    if (this.completionTimeout) {
      clearTimeout(this.completionTimeout);
      this.completionTimeout = null;
    }
    if (this.activeRequestCancelSource) {
      this.activeRequestCancelSource.cancel('Download cancelled');
      this.activeRequestCancelSource = null;
    }

    await this.stubCancelDownloadRequest();

    if (activeDownload) {
      activeDownload.onUpdate('', false);
      downloadRegistry.delete(downloadKey);
    } else {
      this.updateDownloadState('', false);
    }

    if (this._isMounted) {
      this.setState({ downloading: false, downloadStatus: '', pollInterval: null, isModalOpen: false });
    }

    if (this.props.onCancelComplete) {
      this.props.onCancelComplete();
    }
  }

  componentDidMount() {
    this._isMounted = true;
    
    // Check if there's an ongoing download for this component
    const downloadKey = getDownloadKey(this.props);
    const existingDownload = downloadRegistry.get(downloadKey);
    
    if (existingDownload) {
      // Restore the download state
      this.setState({
        downloading: existingDownload.downloading,
        downloadStatus: existingDownload.status
      });
      this.updateDownloadState(existingDownload.status, existingDownload.downloading);
      
      // Update the callback for this component instance
      existingDownload.onUpdate = (status: string, downloading: boolean) => {
        this.updateDownloadState(status, downloading);
      };
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
    // Clear any polling interval when component unmounts
    if (this.state.pollInterval) {
      clearInterval(this.state.pollInterval);
    }
    if (this.completionTimeout) {
      clearTimeout(this.completionTimeout);
    }

  }

  componentDidUpdate(prevProps) {
    if (this.props.cancelRequestId && this.props.cancelRequestId !== prevProps.cancelRequestId && this.props.isActiveDownload) {
      this.cancelDownload();
    }
  }

  render() {
    const { imageType, downloading, isModalOpen, downloadStatus } = this.state;
    const showInlineStatus = !this.props.hideStatus;
    const spinner = <Spinner size="md" />;
    
    const controllerIcon = (
      <div>
        <a onClick={this.handleDownload} data-testid="download-link" style={{ cursor: 'pointer' }}>
          {/* @ts-ignore */}
          <MdCloudDownload size={20} />
        </a>
      </div>
    );
    const linkTrigger = (
      <span className="d-inline-flex align-items-center">
        <a
          onClick={downloading || this.props.disabled ? undefined : this.handleDownload}
          data-testid="download-link"
          style={{
            cursor: downloading || this.props.disabled ? 'default' : 'pointer',
            color: downloading || this.props.disabled ? '#8a8d90' : '#009876',
            fontWeight: 600,
            textDecoration: 'none',
            opacity: downloading || this.props.disabled ? 0.75 : 1,
            pointerEvents: downloading || this.props.disabled ? 'none' : 'auto',
          }}
          aria-disabled={downloading || this.props.disabled}
        >
          {this.props.linkLabel || this.props.buttonLabel || 'Download'}
        </a>
        {downloading && <span style={{ marginLeft: '8px' }}>{spinner}</span>}
      </span>
    );
    const serverIcon = this.props.renderAsLink ? linkTrigger : this.props.buttonLabel ? (
      <Button
        onClick={this.handleDownload}
        data-testid="download-link"
        className="ethica-button-green"
        size={"sm" as unknown as number}
      >
        {React.createElement(MdCloudDownload as unknown as React.ComponentType<{
          size?: number;
          style?: React.CSSProperties;
        }>, { size: 16, style: { marginRight: '6px', verticalAlign: 'middle' } })}
        {this.props.buttonLabel}
      </Button>
    ) : (
      <div>
        <a onClick={this.handleDownload} data-testid="download-link" style={{ cursor: 'pointer' }}>
          {/* @ts-ignore */}
          <MdCloudDownload size={20} />
        </a>
      </div>
    );

    return (
      <div>
        {this.props.renderAsLink ? (
          <div>{serverIcon}</div>
        ) : imageType === "controller" ?
          downloading ? (
            <div>
              <Spinner size="md" />
              {showInlineStatus && downloadStatus && <div data-testid="download-status" className="download-status">{downloadStatus}</div>}
            </div>
          ) : controllerIcon
        :
          downloading ? (
            <div>
              <Spinner size="md" />
              {showInlineStatus && downloadStatus && <div data-testid="download-status" className="download-status">{downloadStatus}</div>}
            </div>
          ) : serverIcon
        }

        <Modal
          variant={ModalVariant.small}
          title="Select Download Type"
          isOpen={isModalOpen}
          onClose={this.handleModalClose}
          className="modal-fade-in"
          actions={[
            <Button 
              key="caimage" 
              variant="primary" 
              onClick={() => this.startDownload("CAImage")}
              className="ethica-button-green"
              data-testid="caimage-button"
            >
              CAImage
            </Button>,
            <Button
              key="caflex"
              variant="primary"
              onClick={() => this.startDownload("CAFlex")}
              className="ethica-button-green"
              data-testid="caflex-button"
            >
              CAFlex
            </Button>,
            <Button
              key="caflexarm64"
              variant="primary"
              onClick={() => this.startDownload("CAFlexARM64")}
              className="ethica-button-green"
              data-testid="caflexarm64-button"
            >
              CAFlex ARM64
            </Button>,
            <Button
              key="cancel"
              variant="primary" 
              onClick={this.handleModalClose}
              className="ethica-button-cancel"
              data-testid="cancel-button"
            >
              Cancel
            </Button>
          ]}
        >
          Please select the type of image to download
        </Modal>
      </div>
    );
  }
}

// Create a wrapper component to provide the snackbar context
const DownloadImageWithSnackbar = React.memo((props: any) => {
  const { enqueueSnackbar } = useSnackbar();
  return <DownloadImage {...props} enqueueSnackbar={enqueueSnackbar} />;
});

DownloadImageWithSnackbar.displayName = 'DownloadImageWithSnackbar';

export default DownloadImageWithSnackbar;
